import { Engine, Scene, Vector3, HemisphericLight, DirectionalLight, ArcRotateCamera, MeshBuilder, StandardMaterial, Color3, Color4, ShadowGenerator, PointerEventTypes, Matrix, GlowLayer, DynamicTexture } from '@babylonjs/core';
import { NetworkManager } from './network/NetworkManager';
import { PlayerEntity } from './entities/Player';
import { RemotePlayerEntity } from './entities/RemotePlayer';
import { MonsterEntity } from './entities/Monster';
import { SkillEffects } from './combat/SkillEffect';
import { HUD } from './ui/HUD';
import { getSkillsByFaction } from '@shared/skillDefs';
import { MOVE_SYNC_RATE } from '@shared/constants';
export class Game {
    engine;
    scene;
    camera;
    network = new NetworkManager();
    player = null;
    remotePlayers = new Map();
    monsters = new Map();
    skillEffects;
    hud;
    ground;
    // 输入状态
    keys = {};
    selectedTarget = null;
    faction = 'sword';
    playerName = '';
    // 面板状态
    panelOpen = null;
    init() {
        this.setupLogin();
    }
    // ========== 登录界面 ==========
    setupLogin() {
        const factionBtns = document.querySelectorAll('.faction-btn');
        factionBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                factionBtns.forEach(b => b.classList.remove('selected'));
                btn.classList.add('selected');
                this.faction = btn.getAttribute('data-faction');
            });
        });
        document.getElementById('enterBtn').addEventListener('click', () => {
            const nameInput = document.getElementById('nameInput');
            this.playerName = nameInput.value.trim() || `侠客${Math.floor(Math.random() * 9999)}`;
            this.startGame();
        });
    }
    // ========== 启动游戏 ==========
    async startGame() {
        document.getElementById('loginUI').style.display = 'none';
        document.getElementById('gameHUD').style.display = 'block';
        const canvas = document.getElementById('renderCanvas');
        this.engine = new Engine(canvas, true, { stencil: true });
        this.scene = new Scene(this.engine);
        this.scene.clearColor = new Color4(0.4, 0.6, 0.85, 1);
        this.scene.ambientColor = new Color3(0.4, 0.4, 0.4);
        this.scene.collisionsEnabled = true;
        this.scene.fogMode = Scene.FOGMODE_LINEAR;
        this.scene.fogStart = 80;
        this.scene.fogEnd = 150;
        this.scene.fogColor = new Color3(0.4, 0.6, 0.85);
        // 辉光效果
        const glow = new GlowLayer('glow', this.scene);
        glow.intensity = 0.3;
        this.createCamera(canvas);
        this.createLights();
        this.createTerrain();
        this.createSkybox();
        this.skillEffects = new SkillEffects(this.scene);
        this.hud = new HUD(this.faction);
        this.setupInput();
        this.engine.runRenderLoop(() => {
            this.update();
            this.scene.render();
        });
        window.addEventListener('resize', () => this.engine.resize());
        try {
            await this.connectServer();
        }
        catch (err) {
            console.error('连接服务器失败:', err);
            alert('无法连接游戏服务器，请确认服务器已启动（npm run dev:server）');
        }
    }
    // ========== 摄像机 ==========
    createCamera(canvas) {
        this.camera = new ArcRotateCamera('camera', -Math.PI / 2, Math.PI / 3.5, 20, Vector3.Zero(), this.scene);
        this.camera.lowerRadiusLimit = 5;
        this.camera.upperRadiusLimit = 40;
        this.camera.lowerBetaLimit = 0.3;
        this.camera.upperBetaLimit = Math.PI / 2.2;
        this.camera.attachControl(canvas, true);
        // 右键拖拽旋转视角
        this.camera.inputs.attached.pointers.buttons = [2];
        this.camera.panningSensibility = 0; // 禁止平移，只旋转+缩放
        this.camera.angularSensibilityX = 600;
        this.camera.angularSensibilityY = 600;
    }
    // ========== 灯光 ==========
    createLights() {
        const ambient = new HemisphericLight('ambient', new Vector3(0, 1, 0), this.scene);
        ambient.intensity = 0.6;
        ambient.groundColor = new Color3(0.3, 0.25, 0.2);
        const sun = new DirectionalLight('sun', new Vector3(-1, -3, -1).normalize(), this.scene);
        sun.intensity = 0.9;
        sun.position = new Vector3(50, 80, 50);
        const shadowGen = new ShadowGenerator(2048, sun);
        shadowGen.useBlurExponentialShadowMap = true;
        shadowGen.blurKernel = 32;
    }
    // ========== 地形 ==========
    createTerrain() {
        this.ground = MeshBuilder.CreateGround('ground', { width: 200, height: 200, subdivisions: 64 }, this.scene);
        const groundMat = new StandardMaterial('groundMat', this.scene);
        // 程序化地面纹理
        const groundTex = new DynamicTexture('groundTex', 512, this.scene, false);
        const ctx = groundTex.getContext();
        // 绘制草地纹理
        ctx.fillStyle = '#2d5a1e';
        ctx.fillRect(0, 0, 512, 512);
        for (let i = 0; i < 3000; i++) {
            const gx = Math.random() * 512;
            const gy = Math.random() * 512;
            ctx.fillStyle = `hsl(${100 + Math.random() * 40}, ${40 + Math.random() * 30}%, ${20 + Math.random() * 20}%)`;
            ctx.fillRect(gx, gy, 1 + Math.random() * 3, 1 + Math.random() * 3);
        }
        // 泥土路径
        ctx.strokeStyle = '#8B7355';
        ctx.lineWidth = 8;
        ctx.beginPath();
        ctx.moveTo(0, 256);
        ctx.quadraticCurveTo(256, 200, 512, 256);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(256, 0);
        ctx.quadraticCurveTo(280, 256, 256, 512);
        ctx.stroke();
        groundTex.update();
        groundMat.diffuseTexture = groundTex;
        groundMat.diffuseTexture.uScale = 10;
        groundMat.diffuseTexture.vScale = 10;
        groundMat.specularColor = new Color3(0.05, 0.05, 0.05);
        this.ground.material = groundMat;
        this.ground.receiveShadows = true;
        // 树木
        for (let i = 0; i < 60; i++) {
            const x = (Math.random() - 0.5) * 180;
            const z = (Math.random() - 0.5) * 180;
            if (Math.abs(x) < 15 && Math.abs(z) < 15)
                continue;
            this.createTree(x, z);
        }
        // 竹子丛
        for (let i = 0; i < 15; i++) {
            const cx = (Math.random() - 0.5) * 160;
            const cz = (Math.random() - 0.5) * 160;
            if (Math.abs(cx) < 20 && Math.abs(cz) < 20)
                continue;
            for (let j = 0; j < 5; j++) {
                this.createBamboo(cx + (Math.random() - 0.5) * 3, cz + (Math.random() - 0.5) * 3);
            }
        }
        // 石头
        for (let i = 0; i < 25; i++) {
            const x = (Math.random() - 0.5) * 180;
            const z = (Math.random() - 0.5) * 180;
            this.createRock(x, z);
        }
        // 武侠场景建筑
        this.createPond(15, -15);
        this.createBridge(15, -10);
        this.createGazebo(-15, 15);
        this.createStoneGate(0, 25);
        this.createLanterns();
    }
    createTree(x, z) {
        const h = 3 + Math.random() * 3;
        const trunk = MeshBuilder.CreateCylinder('tree', { height: h, diameterTop: 0.2, diameterBottom: 0.5 + Math.random() * 0.2 }, this.scene);
        trunk.position = new Vector3(x, h / 2, z);
        const trunkMat = new StandardMaterial('trunkMat', this.scene);
        trunkMat.diffuseColor = new Color3(0.35 + Math.random() * 0.1, 0.2, 0.08);
        trunk.material = trunkMat;
        // 多层树冠
        const layers = 2 + Math.floor(Math.random() * 2);
        for (let l = 0; l < layers; l++) {
            const leafSize = (2.5 - l * 0.5) + Math.random();
            const leaves = MeshBuilder.CreateSphere('leaves', { diameter: leafSize, segments: 6 }, this.scene);
            leaves.position = new Vector3(x + (Math.random() - 0.5) * 0.5, h + l * 0.8, z + (Math.random() - 0.5) * 0.5);
            const leavesMat = new StandardMaterial('leavesMat', this.scene);
            leavesMat.diffuseColor = new Color3(0.08 + Math.random() * 0.05, 0.35 + Math.random() * 0.25, 0.05 + Math.random() * 0.05);
            leaves.material = leavesMat;
        }
    }
    createBamboo(x, z) {
        const h = 4 + Math.random() * 4;
        const bamboo = MeshBuilder.CreateCylinder('bamboo', { height: h, diameter: 0.08 + Math.random() * 0.04 }, this.scene);
        bamboo.position = new Vector3(x, h / 2, z);
        const mat = new StandardMaterial('bambooMat', this.scene);
        mat.diffuseColor = new Color3(0.3, 0.55 + Math.random() * 0.15, 0.2);
        bamboo.material = mat;
    }
    createRock(x, z) {
        const size = 0.3 + Math.random() * 0.8;
        const rock = MeshBuilder.CreatePolyhedron('rock', { size, type: Math.floor(Math.random() * 3) }, this.scene);
        rock.position = new Vector3(x, size * 0.4, z);
        rock.rotation.y = Math.random() * Math.PI * 2;
        rock.rotation.x = Math.random() * 0.3;
        const rockMat = new StandardMaterial('rockMat', this.scene);
        const g = 0.35 + Math.random() * 0.15;
        rockMat.diffuseColor = new Color3(g, g * 0.95, g * 0.85);
        rock.material = rockMat;
    }
    // ========== 池塘 ==========
    createPond(cx, cz) {
        const water = MeshBuilder.CreateDisc('pond', { radius: 6, tessellation: 32 }, this.scene);
        water.position = new Vector3(cx, 0.02, cz);
        water.rotation.x = Math.PI / 2;
        const waterMat = new StandardMaterial('waterMat', this.scene);
        waterMat.diffuseColor = new Color3(0.15, 0.35, 0.5);
        waterMat.emissiveColor = new Color3(0.05, 0.12, 0.18);
        waterMat.alpha = 0.75;
        waterMat.specularColor = new Color3(0.5, 0.5, 0.5);
        waterMat.backFaceCulling = false;
        water.material = waterMat;
        // 池塘边石头
        for (let i = 0; i < 12; i++) {
            const a = (i / 12) * Math.PI * 2;
            const r = 5.5 + Math.random() * 1.5;
            const s = 0.3 + Math.random() * 0.4;
            const rock = MeshBuilder.CreatePolyhedron('pondRock', { size: s, type: 1 }, this.scene);
            rock.position = new Vector3(cx + Math.cos(a) * r, s * 0.3, cz + Math.sin(a) * r);
            rock.rotation.y = Math.random() * Math.PI * 2;
            const mat = new StandardMaterial('pondRockMat', this.scene);
            const g = 0.35 + Math.random() * 0.1;
            mat.diffuseColor = new Color3(g, g * 0.9, g * 0.8);
            rock.material = mat;
        }
        // 荷花（几片圆盘）
        for (let i = 0; i < 5; i++) {
            const a = Math.random() * Math.PI * 2;
            const r = Math.random() * 4;
            const lily = MeshBuilder.CreateDisc('lily', { radius: 0.3 + Math.random() * 0.2, tessellation: 8 }, this.scene);
            lily.position = new Vector3(cx + Math.cos(a) * r, 0.04, cz + Math.sin(a) * r);
            lily.rotation.x = Math.PI / 2;
            const lilyMat = new StandardMaterial('lilyMat', this.scene);
            lilyMat.diffuseColor = new Color3(0.2, 0.6, 0.2);
            lilyMat.backFaceCulling = false;
            lily.material = lilyMat;
            // 花朵
            const flower = MeshBuilder.CreateSphere('flower', { diameter: 0.15, segments: 6 }, this.scene);
            flower.position = new Vector3(cx + Math.cos(a) * r, 0.1, cz + Math.sin(a) * r);
            const flowerMat = new StandardMaterial('flowerMat', this.scene);
            flowerMat.diffuseColor = new Color3(1, 0.6, 0.7);
            flowerMat.emissiveColor = new Color3(0.2, 0.1, 0.12);
            flower.material = flowerMat;
        }
    }
    // ========== 石拱桥 ==========
    createBridge(cx, cz) {
        const bridgeMat = new StandardMaterial('bridgeMat', this.scene);
        bridgeMat.diffuseColor = new Color3(0.55, 0.5, 0.45);
        // 桥面（拱形 = 多段box）
        for (let i = -4; i <= 4; i++) {
            const t = i / 4;
            const y = 0.3 + (1 - t * t) * 0.8; // 抛物线拱形
            const seg = MeshBuilder.CreateBox('bridgeSeg', { width: 2.5, height: 0.15, depth: 1.2 }, this.scene);
            seg.position = new Vector3(cx, y, cz + i * 1.1);
            seg.rotation.x = -t * 0.15; // 轻微倾斜
            // 独立mesh，不需要parent
            seg.material = bridgeMat;
        }
        // 栏杆柱子
        const railMat = new StandardMaterial('railMat', this.scene);
        railMat.diffuseColor = new Color3(0.6, 0.55, 0.5);
        for (let i = -3; i <= 3; i++) {
            const t = i / 4;
            const y = 0.3 + (1 - t * t) * 0.8;
            [-1.1, 1.1].forEach(xOff => {
                const post = MeshBuilder.CreateCylinder('railPost', { height: 0.6, diameter: 0.08, tessellation: 6 }, this.scene);
                post.position = new Vector3(cx + xOff, y + 0.3, cz + i * 1.4);
                post.material = railMat;
            });
        }
    }
    // ========== 凉亭 ==========
    createGazebo(cx, cz) {
        const woodMat = new StandardMaterial('gazeboWood', this.scene);
        woodMat.diffuseColor = new Color3(0.5, 0.28, 0.12);
        const roofMat = new StandardMaterial('gazeboRoof', this.scene);
        roofMat.diffuseColor = new Color3(0.25, 0.15, 0.08);
        // 地基（六角形）
        const base = MeshBuilder.CreateCylinder('gazeboBase', { height: 0.15, diameter: 5, tessellation: 6 }, this.scene);
        base.position = new Vector3(cx, 0.075, cz);
        const baseMat = new StandardMaterial('baseMat', this.scene);
        baseMat.diffuseColor = new Color3(0.5, 0.48, 0.45);
        base.material = baseMat;
        // 6根柱子
        for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2;
            const px = cx + Math.cos(a) * 2;
            const pz = cz + Math.sin(a) * 2;
            const pillar = MeshBuilder.CreateCylinder('pillar', { height: 3, diameter: 0.18, tessellation: 8 }, this.scene);
            pillar.position = new Vector3(px, 1.65, pz);
            pillar.material = woodMat;
        }
        // 屋顶（六角锥）
        const roof = MeshBuilder.CreateCylinder('roof', {
            height: 1.2, diameterTop: 0, diameterBottom: 6, tessellation: 6
        }, this.scene);
        roof.position = new Vector3(cx, 3.75, cz);
        roof.material = roofMat;
        // 檐角翘起（用小圆锥装饰）
        for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
            const tip = MeshBuilder.CreateCylinder('eaveTip', { height: 0.3, diameterTop: 0.02, diameterBottom: 0.12, tessellation: 4 }, this.scene);
            tip.position = new Vector3(cx + Math.cos(a) * 3, 3.3, cz + Math.sin(a) * 3);
            tip.rotation.z = Math.cos(a) * 0.4;
            tip.rotation.x = -Math.sin(a) * 0.4;
            tip.material = roofMat;
        }
    }
    // ========== 石门牌坊 ==========
    createStoneGate(cx, cz) {
        const stoneMat = new StandardMaterial('gateStoneMat', this.scene);
        stoneMat.diffuseColor = new Color3(0.6, 0.58, 0.55);
        stoneMat.specularColor = new Color3(0.15, 0.15, 0.15);
        // 左右石柱
        [-2.5, 2.5].forEach(xOff => {
            const pillar = MeshBuilder.CreateBox('gatePillar', { width: 0.6, height: 4.5, depth: 0.6 }, this.scene);
            pillar.position = new Vector3(cx + xOff, 2.25, cz);
            pillar.material = stoneMat;
            // 柱头装饰
            const cap = MeshBuilder.CreateBox('pillarCap', { width: 0.8, height: 0.2, depth: 0.8 }, this.scene);
            cap.position = new Vector3(cx + xOff, 4.5, cz);
            cap.material = stoneMat;
        });
        // 横梁
        const beam = MeshBuilder.CreateBox('gateBeam', { width: 5.8, height: 0.4, depth: 0.35 }, this.scene);
        beam.position = new Vector3(cx, 4.0, cz);
        beam.material = stoneMat;
        // 上层横梁
        const topBeam = MeshBuilder.CreateBox('gateTopBeam', { width: 4.5, height: 0.25, depth: 0.3 }, this.scene);
        topBeam.position = new Vector3(cx, 4.7, cz);
        beam.material = stoneMat;
        topBeam.material = stoneMat;
        // 牌坊匾额
        const plaque = MeshBuilder.CreateBox('plaque', { width: 2.5, height: 0.8, depth: 0.1 }, this.scene);
        plaque.position = new Vector3(cx, 4.2, cz + 0.2);
        const plaqueMat = new StandardMaterial('plaqueMat', this.scene);
        plaqueMat.diffuseColor = new Color3(0.6, 0.15, 0.08);
        plaque.material = plaqueMat;
    }
    // ========== 灯笼 ==========
    createLanterns() {
        const positions = [
            new Vector3(-3, 0, 25), new Vector3(3, 0, 25), // 石门两侧
            new Vector3(-15, 0, 12), new Vector3(-15, 0, 18), // 凉亭附近
        ];
        const lanternMat = new StandardMaterial('lanternMat', this.scene);
        lanternMat.diffuseColor = new Color3(0.85, 0.15, 0.05);
        lanternMat.emissiveColor = new Color3(0.5, 0.08, 0.02);
        const poleMat = new StandardMaterial('poleMat', this.scene);
        poleMat.diffuseColor = new Color3(0.35, 0.2, 0.1);
        positions.forEach(pos => {
            // 灯柱
            const pole = MeshBuilder.CreateCylinder('lanternPole', { height: 3.5, diameter: 0.08 }, this.scene);
            pole.position = new Vector3(pos.x, 1.75, pos.z);
            pole.material = poleMat;
            // 灯笼主体
            const lantern = MeshBuilder.CreateCylinder('lantern', {
                height: 0.6, diameterTop: 0.25, diameterBottom: 0.35, tessellation: 8
            }, this.scene);
            lantern.position = new Vector3(pos.x, 3.3, pos.z);
            lantern.material = lanternMat;
            // 顶盖
            const top = MeshBuilder.CreateCylinder('lanternTop', { height: 0.08, diameterTop: 0.15, diameterBottom: 0.3, tessellation: 8 }, this.scene);
            top.position = new Vector3(pos.x, 3.65, pos.z);
            top.material = poleMat;
        });
    }
    // ========== 天空盒 ==========
    createSkybox() {
        const skybox = MeshBuilder.CreateBox('skyBox', { size: 800 }, this.scene);
        const skyMat = new StandardMaterial('skyMat', this.scene);
        skyMat.backFaceCulling = false;
        skyMat.disableLighting = true;
        // 渐变天空
        skyMat.emissiveColor = new Color3(0.4, 0.6, 0.85);
        skybox.material = skyMat;
        skybox.infiniteDistance = true;
        skybox.isPickable = false;
    }
    // ========== 输入 ==========
    setupInput() {
        window.addEventListener('keydown', (e) => {
            if (!e.key)
                return;
            if (document.activeElement?.id === 'chatInput') {
                if (e.key === 'Enter')
                    this.hud.sendChat(this.network);
                return;
            }
            const key = e.key.toLowerCase();
            this.keys[key] = true;
            this.handleSkillKey(key);
            // C: 角色面板，B: 背包，K: 技能面板
            if (key === 'c')
                this.hud.togglePanel('character');
            if (key === 'b')
                this.hud.togglePanel('inventory');
            if (key === 'k')
                this.hud.togglePanel('skill');
        });
        window.addEventListener('keyup', (e) => {
            if (!e.key)
                return;
            this.keys[e.key.toLowerCase()] = false;
        });
        // 左键选择目标
        this.scene.onPointerObservable.add((info) => {
            if (info.type === PointerEventTypes.POINTERPICK && info.pickInfo?.hit) {
                const mesh = info.pickInfo.pickedMesh;
                if (mesh)
                    this.handleTargetSelect(mesh);
            }
        });
        // PK 按钮
        window.addEventListener('toggle_pk', () => {
            this.network.sendTogglePK();
        });
        // 聊天按钮
        window.addEventListener('send_chat', () => {
            this.hud.sendChat(this.network);
        });
        setInterval(() => this.syncMovement(), MOVE_SYNC_RATE);
    }
    handleSkillKey(key) {
        if (!this.player)
            return;
        const skills = getSkillsByFaction(this.faction);
        if (key === ' ') {
            this.handleBasicAttack();
            return;
        }
        const keyMap = { '1': 1, '2': 2, '3': 3, '4': 4, '5': 5 };
        const idx = keyMap[key];
        if (idx !== undefined && skills[idx]) {
            this.network.sendAttack({
                skillId: skills[idx].id,
                targetId: this.selectedTarget || undefined,
            });
            this.skillEffects.playSkillEffect(skills[idx], this.player.mesh.position);
        }
    }
    handleTargetSelect(mesh) {
        for (const [id, rp] of this.remotePlayers) {
            if (rp.mesh === mesh || rp.mesh.getChildMeshes().includes(mesh)) {
                this.selectedTarget = id;
                this.hud.showTarget(rp.name, rp.hp, rp.maxHp);
                return;
            }
        }
        for (const [id, m] of this.monsters) {
            if (m.mesh === mesh || m.mesh.getChildMeshes().includes(mesh)) {
                this.selectedTarget = id;
                this.hud.showTarget(m.type, m.hp, m.maxHp);
                return;
            }
        }
        this.selectedTarget = null;
        this.hud.hideTarget();
    }
    handleBasicAttack() {
        if (!this.player)
            return;
        const skills = getSkillsByFaction(this.faction);
        const basicSkill = skills[0];
        // 总是播放本地特效
        this.skillEffects.playSkillEffect(basicSkill, this.player.mesh.position);
        // 有目标时才发送服务器攻击
        if (this.selectedTarget) {
            this.network.sendAttack({
                skillId: basicSkill.id,
                targetId: this.selectedTarget,
            });
        }
    }
    // ========== 网络连接 ==========
    async connectServer() {
        this.network.onWorldSnapshot = (data) => {
            console.log('[Game] 收到世界快照');
            for (const pData of data.players) {
                if (pData.id === this.network.sessionId) {
                    this.player = new PlayerEntity(this.scene, pData, this.faction);
                    this.camera.target = new Vector3(pData.x, 1, pData.z);
                    this.hud.updatePlayerInfo(pData.name, pData.level);
                    this.hud.updateHP(pData.hp, pData.maxHp);
                    this.hud.updateMP(pData.mp, pData.maxMp);
                    console.log('[Game] 本地玩家已创建:', pData.name, '位置:', pData.x, pData.z);
                }
                else {
                    const rp = new RemotePlayerEntity(this.scene, pData);
                    this.remotePlayers.set(pData.id, rp);
                }
            }
            for (const mData of data.monsters) {
                const m = new MonsterEntity(this.scene, mData);
                this.monsters.set(mData.id, m);
            }
        };
        this.network.onPlayerJoin = (pData) => {
            if (pData.id !== this.network.sessionId && !this.remotePlayers.has(pData.id)) {
                const rp = new RemotePlayerEntity(this.scene, pData);
                this.remotePlayers.set(pData.id, rp);
            }
        };
        this.network.onPlayerLeave = (id) => {
            const rp = this.remotePlayers.get(id);
            if (rp) {
                rp.dispose();
                this.remotePlayers.delete(id);
            }
        };
        this.network.onStateUpdate = (data) => {
            for (const pData of data.players) {
                if (pData.id === this.network.sessionId && this.player) {
                    this.player.updateFromServer(pData);
                    this.hud.updateHP(pData.hp, pData.maxHp);
                    this.hud.updateMP(pData.mp, pData.maxMp);
                    this.hud.updatePlayerInfo(pData.name, pData.level);
                    this.hud.updateCharStats(pData);
                }
                else {
                    const rp = this.remotePlayers.get(pData.id);
                    if (rp)
                        rp.updateFromServer(pData);
                    if (pData.id === this.selectedTarget) {
                        this.hud.showTarget(pData.name, pData.hp, pData.maxHp);
                    }
                }
            }
            for (const mData of data.monsters) {
                const m = this.monsters.get(mData.id);
                if (m) {
                    m.updateFromServer(mData);
                    if (mData.id === this.selectedTarget) {
                        this.hud.showTarget(mData.type, mData.hp, mData.maxHp);
                    }
                }
            }
        };
        this.network.onMonsterSpawn = (data) => {
            const m = new MonsterEntity(this.scene, data.monster);
            this.monsters.set(data.monster.id, m);
        };
        this.network.onMonsterKilled = (data) => {
            const m = this.monsters.get(data.monsterId);
            if (m) {
                m.dispose();
                this.monsters.delete(data.monsterId);
            }
        };
        this.network.onDamage = (data) => {
            this.hud.showDamagePopup(data.damage, data.isCrit, this.getDamageScreenPos(data.targetId));
            // 受击闪红
            this.flashHitMesh(data.targetId);
            // 自己被打时屏幕震动
            if (data.targetId === this.network.sessionId) {
                this.shakeCamera(data.isCrit ? 0.5 : 0.2);
            }
        };
        this.network.onSkillCast = (data) => {
            if (data.casterId !== this.network.sessionId) {
                const caster = this.remotePlayers.get(data.casterId);
                if (caster) {
                    const skill = getSkillsByFaction(caster.faction).find((s) => s.id === data.skillId);
                    if (skill)
                        this.skillEffects.playSkillEffect(skill, caster.mesh.position);
                }
            }
        };
        this.network.onPlayerKilled = (data) => {
            this.hud.showKillNotice(data.killerName, data.deadName);
        };
        this.network.onChat = (data) => {
            this.hud.addChatMessage(data.name, data.content);
        };
        await this.network.joinWorld(this.playerName, this.faction);
    }
    // ========== 受击闪红 ==========
    flashHitMesh(targetId) {
        let targetMesh = null;
        if (this.player && targetId === this.network.sessionId) {
            targetMesh = this.player.mesh;
        }
        const rp = this.remotePlayers.get(targetId);
        if (rp)
            targetMesh = rp.mesh;
        const m = this.monsters.get(targetId);
        if (m)
            targetMesh = m.mesh;
        if (!targetMesh)
            return;
        // 遍历所有子网格，设置为红色闪烁
        const allMeshes = [targetMesh, ...targetMesh.getChildMeshes()];
        const originalColors = new Map();
        for (const mesh of allMeshes) {
            const mat = mesh.material;
            if (mat && mat.emissiveColor) {
                originalColors.set(mat, {
                    emissive: mat.emissiveColor.clone(),
                    diffuse: mat.diffuseColor?.clone() || Color3.White(),
                });
                mat.emissiveColor = new Color3(1, 0.2, 0.2);
            }
        }
        // 150ms 后恢复
        setTimeout(() => {
            for (const [mat, colors] of originalColors) {
                mat.emissiveColor = colors.emissive;
            }
        }, 150);
    }
    // ========== 屏幕震动 ==========
    shakeCamera(intensity) {
        const origTarget = this.camera.target.clone();
        let elapsed = 0;
        const duration = 200;
        const interval = setInterval(() => {
            elapsed += 20;
            const decay = 1 - elapsed / duration;
            const ox = (Math.random() - 0.5) * intensity * decay;
            const oy = (Math.random() - 0.5) * intensity * decay;
            this.camera.target.copyFrom(origTarget.add(new Vector3(ox, oy, 0)));
            if (elapsed >= duration) {
                clearInterval(interval);
                this.camera.target.copyFrom(origTarget);
            }
        }, 20);
    }
    // ========== 获取受击目标的屏幕坐标 ==========
    getDamageScreenPos(targetId) {
        let pos = null;
        if (this.player && targetId === this.network.sessionId) {
            pos = this.player.mesh.position;
        }
        const rp = this.remotePlayers.get(targetId);
        if (rp)
            pos = rp.mesh.position;
        const m = this.monsters.get(targetId);
        if (m)
            pos = m.mesh.position;
        if (pos) {
            const worldPos = pos.add(new Vector3(0, 2.5, 0));
            const screenPos = Vector3.Project(worldPos, Matrix.Identity(), this.scene.getTransformMatrix(), this.camera.viewport.toGlobal(this.engine.getRenderWidth(), this.engine.getRenderHeight()));
            return { x: screenPos.x, y: screenPos.y };
        }
        return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    }
    // ========== 游戏循环 ==========
    update() {
        if (!this.player)
            return;
        let dx = 0, dz = 0;
        if (this.keys['w'] || this.keys['arrowup'])
            dz = 1;
        if (this.keys['s'] || this.keys['arrowdown'])
            dz = -1;
        if (this.keys['a'] || this.keys['arrowleft'])
            dx = -1;
        if (this.keys['d'] || this.keys['arrowright'])
            dx = 1;
        if (dx !== 0 || dz !== 0) {
            const camAngle = this.camera.alpha + Math.PI / 2;
            const moveX = dx * Math.cos(camAngle) - dz * Math.sin(camAngle);
            const moveZ = dx * Math.sin(camAngle) + dz * Math.cos(camAngle);
            const len = Math.sqrt(moveX * moveX + moveZ * moveZ);
            this.player.move(moveX / len, moveZ / len, this.engine.getDeltaTime() / 1000);
        }
        else {
            this.player.idle();
        }
        this.remotePlayers.forEach(rp => rp.interpolate());
        this.monsters.forEach(m => m.interpolate());
        this.hud.updateMinimap(this.player.mesh.position, this.remotePlayers, this.monsters);
        // 摄像机跟随玩家（不用 lockedTarget，保持自由旋转）
        this.camera.target.copyFrom(this.player.mesh.position);
        this.camera.target.y = 1;
    }
    syncMovement() {
        if (!this.player)
            return;
        this.network.sendMove({
            x: this.player.mesh.position.x,
            z: this.player.mesh.position.z,
            rotationY: this.player.mesh.rotation.y,
        });
    }
}
